import { useEffect, useId, useRef, useState, type ReactNode, type KeyboardEvent } from "react";
import clsx from "clsx";

/**
 * Accessible help tooltip: opens on hover, keyboard focus and tap; closes on Escape, blur or outside tap.
 * The trigger is focusable and gets `aria-describedby` pointing at the (always present, possibly hidden) text,
 * so screen readers announce the help even when it is not shown.
 */
export function Tooltip({ text, children, className, triggerClassName, as: Tag = "span" }: {
  text: ReactNode;
  children: ReactNode;
  className?: string;
  triggerClassName?: string;
  as?: "span" | "div";
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => { if (root.current && !root.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") { e.stopPropagation(); setOpen(false); }
  };

  return (
    <Tag ref={root as never} className={clsx("relative inline-flex", className)} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <span tabIndex={0} aria-describedby={id} className={clsx("inline-flex rounded-full focus-ring", triggerClassName)}
        onFocus={() => setOpen(true)} onBlur={() => setOpen(false)} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} onKeyDown={onKey}>
        {children}
      </span>
      <span role="tooltip" id={id} hidden={!open} className="iga-tooltip">{text}</span>
    </Tag>
  );
}
